import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import type { SupplierUploadTracking } from '../../../lib/api/barratt';
import { cn } from '../../../lib/utils';
import { formatDay, isRequested, overdueLabel } from '../uploadCycle';
import type { ObligationPeriod } from './useMyObligations';
import ObligationRow from './ObligationRow';

interface ObligationPeriodCardProps {
  period: ObligationPeriod;
  uploadingId: number | null;
  onUpload: (row: SupplierUploadTracking, file: File) => void;
  onDownloadTemplate: (row: SupplierUploadTracking) => void;
}

/** A month still waiting on a file: its due date, a done-count, and a row per dataset. */
export default function ObligationPeriodCard({
  period,
  uploadingId,
  onUpload,
  onDownloadTemplate,
}: ObligationPeriodCardProps) {
  // Only what the customer actually asked for is counted. The rows minted for
  // months nobody requested still show — a supplier may want to send one — but
  // putting them in the denominator turns "0 of 1" into "0 of 2" and invents
  // work the supplier does not owe.
  const asked = period.rows.filter(isRequested);
  const done = asked.filter((r) => r.status === 'uploaded').length;
  const late = period.rows.some((r) => r.status === 'overdue') ? overdueLabel(period.dueDate) : null;

  return (
    <Card
      className={cn(late && 'border-red-200 dark:border-red-900/70')}
      data-testid={`period-card-${period.key}`}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 p-4 pb-3">
        <div>
          <CardTitle className="text-lg">{period.label}</CardTitle>
          <CardDescription>
            Due by {formatDay(period.dueDate)}
            {late && <span className="font-medium text-red-600 dark:text-red-400"> · {late}</span>}
          </CardDescription>
        </div>
        <Badge variant="outline" className="shrink-0 bg-muted text-muted-foreground">
          {done} of {asked.length} uploaded
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2 p-4 pt-0">
        {period.rows.map((row) => (
          <ObligationRow
            key={row.id}
            row={row}
            uploading={uploadingId === row.id}
            disabled={uploadingId !== null && uploadingId !== row.id}
            periodDueDate={period.dueDate}
            onUpload={(file) => onUpload(row, file)}
            onDownloadTemplate={row.status === 'uploaded' ? undefined : () => onDownloadTemplate(row)}
          />
        ))}
      </CardContent>
    </Card>
  );
}
